import React, { useState } from 'react';
import axios from 'axios';

function trinityStatusMeta(status) {
  switch (status) {
    case 'success': return { label: 'Stored', color: '#276749', background: '#F0FFF4' };
    case 'unmatched_employee': return { label: 'No employee', color: '#975A16', background: '#FFFAF0' };
    case 'ambiguous_employee': return { label: 'Needs match', color: '#975A16', background: '#FFFAF0' };
    case 'not_configured': return { label: 'Not configured', color: '#546E7A', background: '#EEF3F8' };
    case 'pending': return { label: 'Pending', color: '#2B6CB0', background: '#EBF8FF' };
    case 'N/A':
    case '':
    case undefined: return { label: 'No status', color: '#78909C', background: '#F5F7FA' };
    default: return { label: 'Failed', color: '#C53030', background: '#FFF5F5' };
  }
}

export default function Admin() {
  const [key, setKey] = useState('');
  const [authed, setAuthed] = useState(false);
  const [records, setRecords] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const adminConfig = () => ({ headers: { 'x-admin-key': key }, withCredentials: true });

  const loadAccessRequests = async () => {
    const { data } = await axios.get('/api/admin/access-requests', adminConfig());
    setAccessRequests(data.requests || []);
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/api/admin/records', adminConfig());
      setRecords(data.records);
      await loadAccessRequests();
      setAuthed(true);
    } catch (err) {
      setError(
        err.response?.status === 401
          ? 'Incorrect password.'
          : err.response?.status === 503
            ? 'Admin access has not been configured on the server.'
            : 'Failed to load records.'
      );
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    try {
      const { data } = await axios.get('/api/admin/records', adminConfig());
      setRecords(data.records);
      await loadAccessRequests();
    } catch (_) {}
  };

  const hasStoredFile = (fileId) => fileId && fileId !== 'N/A';
  const hasLink = (url) => url && url !== 'N/A';

  const downloadStoredDocument = async (fileId, fallbackName) => {
    try {
      const response = await axios.get(
        `/api/admin/document/${encodeURIComponent(fileId)}`,
        { ...adminConfig(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fallbackName;
      anchor.click();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (_) {
      window.alert('This document could not be downloaded. Please refresh and retry.');
    }
  };

  const filtered = records.filter(r =>
    Object.values(r).some(v => String(v || '').toLowerCase().includes(search.toLowerCase()))
  );

  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#EEF3F8' }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: '48px 40px', width: 380, boxShadow: '0 4px 24px rgba(0,0,0,0.10)' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ width: 52, height: 52, background: '#1B3A5C', borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
              <span style={{ color: '#fff', fontSize: 22 }}>🔐</span>
            </div>
            <h2 style={{ color: '#1B3A5C', fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Admin Dashboard</h2>
            <p style={{ color: '#78909C', fontSize: 13 }}>Training Records — Odyssey Solutions</p>
          </div>
          <form onSubmit={login}>
            <input
              type="password"
              placeholder="Admin password"
              value={key}
              onChange={e => setKey(e.target.value)}
              style={{ width: '100%', padding: '11px 14px', border: '1.5px solid #DDE3ED', borderRadius: 8, fontSize: 14, marginBottom: 14, outline: 'none', boxSizing: 'border-box' }}
              autoFocus
            />
            {error && <p style={{ color: '#C53030', fontSize: 13, marginBottom: 10 }}>{error}</p>}
            <button
              type="submit"
              disabled={loading || !key}
              style={{ width: '100%', padding: '11px', background: '#1B3A5C', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: loading || !key ? 0.6 : 1 }}
            >
              {loading ? 'Loading…' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ color: '#1B3A5C', fontSize: 22, fontWeight: 700, marginBottom: 4 }}>HIPAA Training Records</h1>
          <p style={{ color: '#78909C', fontSize: 13 }}>{records.length} total completion{records.length !== 1 ? 's' : ''}</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <input
            placeholder="Search name, email, office…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '9px 14px', border: '1.5px solid #DDE3ED', borderRadius: 8, fontSize: 13, width: 240, outline: 'none' }}
          />
          <button onClick={refresh} style={{ padding: '9px 16px', background: '#EEF3F8', border: '1px solid #DDE3ED', borderRadius: 8, fontSize: 13, cursor: 'pointer', color: '#1B3A5C', fontWeight: 600 }}>
            Refresh
          </button>
        </div>
      </div>

      <section style={{ marginBottom: 30 }}>
        <h2 style={{ color: '#1B3A5C', fontSize: 17, fontWeight: 700, margin: '0 0 8px' }}>Training-link delivery</h2>
        <p style={{ color: '#78909C', fontSize: 13, margin: '0 0 12px' }}>
          Provider delivery status for recent link requests. Email addresses and links are not shown here.
        </p>
        {accessRequests.length === 0 ? (
          <div style={{ padding: '18px', border: '1px solid #DDE3ED', borderRadius: 10, color: '#78909C', fontSize: 13 }}>
            No training-link requests recorded yet.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid #DDE3ED' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr style={{ background: '#1B3A5C', color: '#fff' }}>
                {['Request', 'Provider status', 'Reason', 'Updated'].map(h => <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600 }}>{h}</th>)}
              </tr></thead>
              <tbody>{accessRequests.map((request, index) => (
                <tr key={request.request_id} style={{ background: index % 2 ? '#F8FAFC' : '#fff', borderTop: '1px solid #EEF3F8' }}>
                  <td style={{ padding: '10px 14px', color: '#546E7A', fontFamily: 'monospace', fontSize: 11 }}>{request.request_id}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: request.provider_status === 'delivered' ? '#276749' : request.provider_status === 'bounced' || request.provider_status === 'suppressed' || request.provider_status === 'failed' ? '#C53030' : '#975A16' }}>{request.provider_status}</td>
                  <td style={{ padding: '10px 14px', color: '#546E7A' }}>{request.provider_reason || '—'}</td>
                  <td style={{ padding: '10px 14px', color: '#78909C', whiteSpace: 'nowrap' }}>{request.provider_updated_at ? new Date(request.provider_updated_at).toLocaleString() : 'Waiting for provider'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </section>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: '#78909C', fontSize: 15 }}>
          {records.length === 0 ? 'No completions recorded yet.' : 'No results match your search.'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #DDE3ED' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#1B3A5C', color: '#fff' }}>
                {['Certificate ID', 'Full Name', 'Email', 'Office', 'Role', 'Completion Date', 'Valid Until', 'Documents', 'TrinityHR', 'Timestamp'].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const trinity = trinityStatusMeta(r['TrinityHR Status']);
                return (
                  <tr key={r['Event ID'] || i} style={{ background: i % 2 === 0 ? '#fff' : '#F8FAFC', borderBottom: '1px solid #EEF3F8' }}>
                  <td style={{ padding: '11px 16px', fontFamily: 'monospace', fontSize: 11, color: '#546E7A' }}>{r['Certificate ID']}</td>
                  <td style={{ padding: '11px 16px', fontWeight: 600, color: '#1B3A5C' }}>{r['Full Name']}</td>
                  <td style={{ padding: '11px 16px', color: '#2C3E50' }}>{r['Email']}</td>
                  <td style={{ padding: '11px 16px', color: '#2C3E50', whiteSpace: 'nowrap' }}>{r['Office Location'] || 'N/A'}</td>
                  <td style={{ padding: '11px 16px' }}>
                    <span style={{ background: '#EEF3F8', color: '#1B3A5C', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {r['Role']}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px', color: '#2C3E50', whiteSpace: 'nowrap' }}>{r['Completion Date']}</td>
                  <td style={{ padding: '11px 16px', color: '#00A3A3', fontWeight: 600, whiteSpace: 'nowrap' }}>{r['Valid Until']}</td>
                  <td style={{ padding: '11px 16px' }}>
                    <div style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
                      {hasStoredFile(r['Certificate File ID']) ? (
                        <button type="button" onClick={() => downloadStoredDocument(r['Certificate File ID'], `${r['Certificate ID'] || 'HIPAA'}-Certificate.pdf`)} style={{ color: '#1B3A5C', fontWeight: 700, border: 0, padding: 0, background: 'none', cursor: 'pointer' }}>Certificate</button>
                      ) : hasLink(r['Certificate Link']) ? (
                        <a href={r['Certificate Link']} target="_blank" rel="noreferrer" style={{ color: '#1B3A5C', fontWeight: 700, textDecoration: 'none' }}>Certificate</a>
                      ) : (
                        <span style={{ color: '#B0BEC5' }}>Certificate</span>
                      )}
                      <span style={{ color: '#DDE3ED' }}>·</span>
                      {hasStoredFile(r['Acknowledgment File ID']) ? (
                        <button type="button" onClick={() => downloadStoredDocument(r['Acknowledgment File ID'], `${r['Certificate ID'] || 'HIPAA'}-Acknowledgment.pdf`)} style={{ color: '#1B3A5C', fontWeight: 700, border: 0, padding: 0, background: 'none', cursor: 'pointer' }}>Acknowledgment</button>
                      ) : hasLink(r['Acknowledgment Link']) ? (
                        <a href={r['Acknowledgment Link']} target="_blank" rel="noreferrer" style={{ color: '#1B3A5C', fontWeight: 700, textDecoration: 'none' }}>Acknowledgment</a>
                      ) : (
                        <span style={{ color: '#B0BEC5' }}>Acknowledgment</span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '11px 16px' }} title={r['TrinityHR Error'] || r['TrinityHR Status'] || ''}>
                    <span style={{ background: trinity.background, color: trinity.color, padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {trinity.label}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px', color: '#90A4AE', fontSize: 11, whiteSpace: 'nowrap' }}>{r['Timestamp']}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ marginTop: 16, fontSize: 11, color: '#B0BEC5', textAlign: 'center' }}>
        Data sourced from Google Sheets — HIPAA Training Log · Odyssey Solutions
      </p>
    </div>
  );
}
